import type { ReactNode } from "react";

import clsx from "clsx";
import { Button } from "react-aria-components";

import { ArrowReloadIcon } from "../../../icons/arrow-reload";
import { TablePagination } from "../table-pagination";

import styles from "./table-footer.module.css";

type TableFooterProps = {
	currentPage?: number;
	isEmptyState: boolean;
	isNextDisabled?: boolean;
	isPrevDisabled?: boolean;
	onPageNavigation?: (dir: "next" | "prev") => void;
	perPage?: number;
	refreshData?: () => void;
	setPerPage?: (perPage: number) => void;
	totalPages?: number;
};

/**
 * @description A component that renders the table footer.
 * It includes buttons for navigating to the next and previous page, refreshing the data, and changing the number of items displayed per page.
 * The buttons are only rendered if the corresponding callback functions are provided as props.
 */
export function TableFooter({
	currentPage,
	isEmptyState,
	isNextDisabled,
	isPrevDisabled,
	onPageNavigation,
	perPage,
	refreshData,
	setPerPage,
	totalPages,
}: TableFooterProps): ReactNode {
	if (!onPageNavigation && !refreshData && !setPerPage) {
		return null;
	}
	return (
		<footer className={styles.footer} data-testid="table-footer">
			<div className={styles.refetchAndNavigation}>
				{refreshData && (
					<Button
						className={styles.refetch}
						data-testid="refresh-button"
						onPress={refreshData}
					>
						<ArrowReloadIcon />
					</Button>
				)}
				{onPageNavigation && (
					<TablePagination
						currentPage={currentPage}
						isEmptyState={isEmptyState}
						isNextDisabled={isNextDisabled}
						isPrevDisabled={isPrevDisabled}
						onPageNavigation={onPageNavigation}
						totalPages={totalPages}
					/>
				)}
			</div>
			<div className={styles.perPage}>
				{!isEmptyState && setPerPage && (
					<>
						<Button
							className={clsx(perPage === 25 && styles.selectedPerPage)}
							data-testid="per-page-25"
							onPress={() => {
								return setPerPage(25);
							}}
						>
							25
						</Button>
						<Button
							className={clsx(perPage === 50 && styles.selectedPerPage)}
							data-testid="per-page-50"
							onPress={() => {
								return setPerPage(50);
							}}
						>
							50
						</Button>
						<Button
							className={clsx(perPage === 100 && styles.selectedPerPage)}
							data-testid="per-page-100"
							onPress={() => {
								return setPerPage(100);
							}}
						>
							100
						</Button>
					</>
				)}
			</div>
		</footer>
	);
}
